import React, { useState } from 'react';
import { Mail, ArrowLeft, CheckCircle } from 'lucide-react';
import { useParams } from 'react-router-dom';
import { forgotPasswordApi } from './forgotPassword';
import toast from 'react-hot-toast';

interface ForgotPasswordProps {
  success: boolean;
  message: string;
}

export default function ForgotPassword() {
  const [email, setEmail] = useState('');
  const [isSubmitted, setIsSubmitted] = useState(false);
  const route = useParams();

  const handleSubmit = async (e:any) => {
    e.preventDefault();
    const resetType = route.resetType;
    
    if (!email) {
      toast.error('Please enter your email address');
      return;
    }
    
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      toast.error('Please enter a valid email address');
      return;
    }
    
    if (resetType) {
      const toastId = toast.loading('Sending reset link...');
      try {
        const res = await forgotPasswordApi(email, resetType) as ForgotPasswordProps;
        if (res.success) {
          toast.success(res.message, { id: toastId });
          setIsSubmitted(true);
        } else {
          toast.error(res.message, { id: toastId });
          setIsSubmitted(false);
        }
      } catch (error: any) {
        toast.error(error.message || 'An unexpected error occurred.', { id: toastId });
        setIsSubmitted(false);
      }
    }
  };

  const handleBackToLogin = () => {
    window.history.back();
  };

  if (isSubmitted) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4" style={{
        background: 'linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%)'
      }}>
        <div className="absolute top-0 left-0 w-64 h-64 bg-teal-200 rounded-full opacity-20 -translate-x-1/2 -translate-y-1/2"></div>
        <div className="absolute bottom-0 right-0 w-96 h-96 bg-cyan-200 rounded-full opacity-20 translate-x-1/3 translate-y-1/3"></div>
        
        <div className="bg-white rounded-2xl shadow-2xl p-8 w-full max-w-md relative z-10">
          <div className="text-center">
            <div className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-r from-teal-400 to-cyan-500 rounded-full mb-6">
              <CheckCircle className="w-10 h-10 text-white" />
            </div>
            
            <h2 className="text-2xl font-bold text-gray-800 mb-3">Check Your Email</h2>
            <p className="text-gray-600 mb-6">
              We've sent a password reset link to <span className="font-semibold text-teal-600">{email}</span>
            </p>
            
            <div className="bg-teal-50 border border-teal-200 rounded-lg p-4 mb-6 text-left">
              <p className="text-sm text-gray-700 mb-2">
                <strong>Didn't receive the email?</strong>
              </p>
              <ul className="text-sm text-gray-600 space-y-1 list-disc list-inside">
                <li>Check your spam or junk folder</li>
                <li>Make sure the email address is correct</li>
                <li>Wait a few minutes and check again</li>
              </ul>
            </div>
            
            <button
              onClick={handleBackToLogin}
              className="w-full py-3 px-4 bg-gradient-to-r from-teal-500 to-cyan-500 text-white rounded-lg font-medium hover:from-teal-600 hover:to-cyan-600 transition-all duration-300 shadow-lg hover:shadow-xl flex items-center justify-center gap-2"
            >
              <ArrowLeft className="w-5 h-5" />
              Back to Login
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center p-4" style={{
      background: 'linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%)'
    }}>
      <div className="absolute top-0 left-0 w-64 h-64 bg-teal-200 rounded-full opacity-20 -translate-x-1/2 -translate-y-1/2"></div>
      <div className="absolute bottom-0 right-0 w-96 h-96 bg-cyan-200 rounded-full opacity-20 translate-x-1/3 translate-y-1/3"></div>
      
      <div className="bg-white rounded-2xl shadow-2xl p-8 w-full max-w-md relative z-10">
        <button
          onClick={handleBackToLogin}
          className="flex items-center gap-2 text-teal-600 hover:text-teal-700 font-medium mb-6 transition-colors duration-200"
        >
          <ArrowLeft className="w-5 h-5" />
          Back to Login
        </button>

        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-r from-yellow-400 to-teal-500 rounded-full mb-4">
            <span className="text-2xl font-bold text-white">M</span>
          </div>
          
          <h1 className="text-3xl font-bold text-gray-800 mb-2">Forgot Password?</h1>
          <p className="text-gray-600">
            No worries! Enter your email and we'll send you reset instructions.
          </p>
        </div>

        <div className="space-y-6">
          <div>
            <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
              Email Address
            </label>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Mail className="h-5 w-5 text-gray-400" />
              </div>
              <input
                type="email"
                id="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleSubmit(e)}
                className="block w-full pl-10 pr-3 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all duration-200"
                placeholder="Enter your email"
              />
            </div>
          </div>

          <button
            onClick={handleSubmit}
            className="w-full py-3 px-4 bg-gradient-to-r from-yellow-400 via-teal-400 to-cyan-500 text-white rounded-lg font-medium hover:shadow-xl transition-all duration-300 transform hover:-translate-y-0.5"
          >
            Send Reset Link →
          </button>
        </div>

        <div className="mt-6 text-center">
          <p className="text-sm text-gray-600">
            Remember your password?{' '}
            <button
              onClick={handleBackToLogin}
              className="text-teal-600 hover:text-teal-700 font-semibold transition-colors duration-200"
            >
              Sign in
            </button>
          </p>
        </div>
      </div>
    </div>
  );
}
